/**
 * Knack PDP gallery cursor (desktop carousel) - SULT-style.
 *
 * Replaces the gallery's fixed left/right arrows with a single arrow that follows the mouse over the
 * image and flips direction by which half it is on: pointing left on the left half (previous) and
 * right on the right half (next). Clicking the image navigates that way.
 *
 * No overlay element: listeners sit on the slideshow container and the floating arrow is
 * pointer-events:none, so it never traps the "+" zoom button. The click is caught in the capture
 * phase and stopped before Horizon's delegated zoom handlers run, so a plain image click navigates
 * instead of zooming - except on the "+" affordance, which is allowed through so tap-to-zoom stays
 * available. Navigation reuses the native prev/next buttons' wired handlers, so it always matches
 * Horizon's behaviour (infinite loop, etc.).
 *
 * Mouse + desktop only: touch pointers and viewports below 750px are ignored, so mobile swipe and
 * the keyboard-reachable native arrows are untouched.
 */
const DESKTOP_QUERY = '(min-width: 750px)';
const ZOOM_SELECTOR = '.knack-gallery-zoom';

class KnackGalleryCursor {
  /** @param {Element} gallery - a media-gallery.media-gallery--carousel element */
  constructor(gallery) {
    this.container = gallery.querySelector('slideshow-container');
    this.prevButton = gallery.querySelector('.slideshow-control--previous');
    this.nextButton = gallery.querySelector('.slideshow-control--next');

    // No container, or a single-image gallery (no arrows) -> nothing to drive.
    if (!this.container || !this.prevButton || !this.nextButton) return;

    this.#build();
    this.#bind();
  }

  #build() {
    this.arrow = document.createElement('div');
    this.arrow.className = 'knack-gallery-cursor';
    this.arrow.setAttribute('aria-hidden', 'true');
    // Base icon points right (next); CSS flips it to point left on the left half (prev).
    this.arrow.innerHTML =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';

    this.container.classList.add('knack-gallery-cursor-area');
    this.container.appendChild(this.arrow);
  }

  #bind() {
    this.container.addEventListener('pointerenter', this.#onEnter);
    this.container.addEventListener('pointermove', this.#onMove);
    this.container.addEventListener('pointerleave', this.#onLeave);
    // Capture phase so we can stop Horizon's delegated zoom click before it fires.
    this.container.addEventListener('click', this.#onClick, true);
  }

  /** @param {PointerEvent} event */
  #usable(event) {
    return event.pointerType !== 'touch' && window.matchMedia(DESKTOP_QUERY).matches;
  }

  /** Over the "+" zoom button we step aside so tap-to-zoom keeps working. @param {Event} event */
  #onZoom(event) {
    return event.target instanceof Element && event.target.closest(ZOOM_SELECTOR);
  }

  #onEnter = (/** @type {PointerEvent} */ event) => {
    if (!this.#usable(event)) return;
    this.#position(event);
    if (!this.#onZoom(event)) this.arrow.classList.add('is-visible');
  };

  #onMove = (/** @type {PointerEvent} */ event) => {
    if (!this.#usable(event) || this.#onZoom(event)) {
      this.arrow.classList.remove('is-visible');
      return;
    }
    this.#position(event);
    this.arrow.classList.add('is-visible');
  };

  #onLeave = () => {
    this.arrow.classList.remove('is-visible');
  };

  #onClick = (/** @type {PointerEvent} */ event) => {
    if (!this.#usable(event) || this.#onZoom(event)) return;
    // Take over the click: navigate instead of opening zoom.
    event.preventDefault();
    event.stopPropagation();
    const { left, width } = this.container.getBoundingClientRect();
    const onLeftHalf = event.clientX - left < width / 2;
    (onLeftHalf ? this.prevButton : this.nextButton).click();
  };

  /** Move the arrow to the cursor and flip it for the half it is on. @param {PointerEvent} event */
  #position(event) {
    const { left, top, width } = this.container.getBoundingClientRect();
    const x = event.clientX - left;
    const y = event.clientY - top;
    this.arrow.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
    this.arrow.classList.toggle('is-prev', x < width / 2);
  }
}

/**
 * Dedupe on the arrow being present in the DOM (not a flag): a variant change re-renders the gallery
 * and wipes the injected arrow, so we rebuild whenever it is missing.
 * @param {ParentNode} [root]
 */
function initKnackGalleryCursor(root = document) {
  for (const gallery of root.querySelectorAll('media-gallery.media-gallery--carousel')) {
    if (gallery.querySelector('.knack-gallery-cursor')) continue;
    new KnackGalleryCursor(gallery);
  }
}

if (document.readyState !== 'loading') {
  initKnackGalleryCursor();
} else {
  document.addEventListener('DOMContentLoaded', () => initKnackGalleryCursor(), { once: true });
}

// Rebuild after a variant change re-renders the gallery media.
document.addEventListener('variant:update', () => requestAnimationFrame(() => initKnackGalleryCursor()));
